// src/schedule/components/EventBox.jsx
import React, { useRef } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { MdPersonAdd, MdSettings } from 'react-icons/md';
import StaffMenu from '../StaffMenu';
import AddStaffPicker from '../AddStaffPicker';
import NeededMenu from '../NeededMenu';
import Avatar from '../Avatar';
import EventActionsMenu from './EventActionsMenu';
import EventNoteEditor from './EventNoteEditor';
import { eventRefFromEv } from '../../utils/dateHelpers';
import {
    confirmStaffAssignmentIfNeeded,
    getStaffAssignmentConflict,
} from '../../utils/staffCopyConflicts';
import { isStaffTentative, staffDisplayName, formatStaffEntryForStore } from '../../utils/staffEntry';
import { isOffListStaffName, staffNameForAssignment } from '../../utils/employees';

/**
 * Komponent reprezentujący pojedyncze wydarzenie w dniu
 */
export default function EventBox({
    evt,
    date,
    evtIndex,
    keyId,
    droppableId,
    employees,
    allEventsForDay,
    isAdmin,
    updateEventStaff,
    updateEventColor,
    updateNeededForEvent,
    activeCalendarId,
    eventsByDateFull,
    unavailableByDate,
    filterUnavailable,
    // Notatki
    readNote,
    writeNote,
    noteText,
    hasNote,
    openNoteKey,
    noteDraft,
    onNoteChange,
    onNoteSave,
    onNoteCancel,
    onNoteClear,
    // Menu akcji
    openMenu,
    menuOverlayRef,
    onMenuToggle,
    onColorClick,
    onCopyClick,
    onNeededClick,
    onNoteClick,
    // Needed menu
    openNeededMenu,
    neededPortalRoot,
    neededPanelRef,
    actionContainersRef,
    onNeededClose,
    onRegisterPanelRef,
    // Mobile expanded
    isExpanded = false,
}) {
    const empList = Array.isArray(employees) ? employees : [];
    
    const currentStaffNames = new Set(
        (evt.staff || []).map((sx) => (typeof sx === 'object' ? sx?.name : sx)).filter(Boolean)
    );

    const available = empList.filter((emp) => {
        const name = typeof emp === 'string' ? emp : emp.name;
        return name && !currentStaffNames.has(name);
    });
    const assignedNames = (evt.staff || []).map(staffDisplayName).filter(Boolean);

    const assignmentCtx = {
        date,
        targetRef: eventRefFromEv(evt, activeCalendarId),
        eventsByDate: eventsByDateFull || {},
        unavailableByDate: unavailableByDate || {},
    };

    const neededNumRaw = Number(evt?.needed);
    const needed = Number.isFinite(neededNumRaw) && neededNumRaw >= 1 ? neededNumRaw : Math.max(1, (evt?.staff?.length || 0));
    const freeSlots = Math.max(0, needed - (evt?.staff?.length || 0));

    return (
        <div
            className={`event-box rounded p-3 shadow-lg relative border ${isExpanded ? 'event-box-expanded' : ''}`}
            style={isExpanded ? {
                // W trybie expanded CSS ma pełną kontrolę - tylko kolor i tło
                borderLeft: `6px solid ${evt.color || '#888888'}`,
                backgroundColor: 'var(--panel-2, var(--tw-colors-gray-900))'
            } : {
                minWidth: '220px',
                flex: '0 0 220px',
                width: '220px',
                borderLeft: `4px solid ${evt.color || '#888888'}`,
                backgroundColor: 'var(--panel-2, var(--tw-colors-gray-900))'
            }}
            data-ph="0"
        >
            <div className="flex justify-between items-center mb-1">
                <div className="event-title font-bold text-sm" title={evt.event}>
                    {evt.event}
                </div>
                {isAdmin && (
                    <div
                        className="relative"
                        ref={(el) => { if (el) actionContainersRef.current[keyId] = el; }}
                    >
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onMenuToggle(keyId);
                            }}
                            className={`btn relative ${hasNote ? 'text-red-500 dark:text-red-400' : ''}`}
                            aria-haspopup="menu"
                            aria-expanded={openMenu === keyId}
                            type="button"
                            title={hasNote ? 'Uwaga: istnieje notatka' : 'Ustawienia'}
                        >
                            <MdSettings />
                            {hasNote && <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>}
                        </button>

                        <EventActionsMenu
                            open={openMenu === keyId}
                            hasNote={hasNote}
                            onColorClick={onColorClick}
                            onCopyClick={onCopyClick}
                            onNeededClick={onNeededClick}
                            onNoteClick={onNoteClick}
                            menuRef={menuOverlayRef}
                        />
                    </div>
                )}
            </div>

            <Droppable droppableId={droppableId}>
                {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                        {(evt.staff || []).map((s, j) => {
                            const sName = staffDisplayName(s);
                            // Użyj indeksu dla draggableId (wymagane przez bibliotekę DnD)
                            const draggableId = `${date}::${evt.event}::${evt.calendarId ?? 'noCal'}::${j}`;
                            // Użyj stabilnego key opartego na nazwie + unikalnym ID eventu
                            // To zapobiega mruganiu przy zmianie kolejności, ale zachowuje stabilność
                            // Jeśli ta sama osoba jest w tym samym evencie, key będzie taki sam
                            const stableKey = `${date}::${evt.event}::${evt.calendarId ?? 'noCal'}::${sName}::${evt.gId || evt.event}`;
                            const empObj = empList.find(
                                (e) => (typeof e === 'string' ? e : e.name) === sName
                            );
                            const show = empObj ? (typeof empObj === 'string' ? { name: empObj } : empObj) : { name: sName };
                            const tentative = isStaffTentative(s);
                            const offList = isOffListStaffName(sName, empList);

                            return (
                                <Draggable key={stableKey} draggableId={draggableId} index={j}>
                                    {(provided2, snapshot) => {
                                        // Poprawka pozycjonowania podczas drag w trybie expanded
                                        const dragStyle = provided2.draggableProps.style || {};
                                        let correctedStyle = { ...dragStyle };
                                        
                                        // W trybie expanded koryguj transform - użyj pozycji kursora zamiast pozycji elementu
                                        if (snapshot.isDragging && isExpanded) {
                                            // Użyj requestAnimationFrame do ciągłej korekty na podstawie pozycji kursora
                                            const element = document.querySelector(`[data-rbd-draggable-id="${draggableId}"]`);
                                            if (element && window.lastMouseEvent) {
                                                // Pobierz początkową pozycję elementu (przy pierwszym renderze podczas drag)
                                                if (!element.dataset.dragStartX) {
                                                    const rect = element.getBoundingClientRect();
                                                    element.dataset.dragStartX = String(rect.left);
                                                    element.dataset.dragStartY = String(rect.top);
                                                    element.dataset.dragMouseStartX = String(window.lastMouseEvent.clientX);
                                                    element.dataset.dragMouseStartY = String(window.lastMouseEvent.clientY);
                                                }
                                                
                                                const startX = parseFloat(element.dataset.dragStartX || '0');
                                                const startY = parseFloat(element.dataset.dragStartY || '0');
                                                const mouseStartX = parseFloat(element.dataset.dragMouseStartX || '0');
                                                const mouseStartY = parseFloat(element.dataset.dragMouseStartY || '0');
                                                
                                                const deltaX = window.lastMouseEvent.clientX - mouseStartX;
                                                const deltaY = window.lastMouseEvent.clientY - mouseStartY;
                                                
                                                const newX = startX + deltaX;
                                                const newY = startY + deltaY - 35; // Offset w górę
                                                
                                                element.style.transform = `translate(${newX}px, ${newY}px)`;
                                                element.style.position = 'fixed';
                                                element.style.zIndex = '2147483649';
                                            } else if (dragStyle.transform) {
                                                // Fallback: parsuj transform z biblioteki i skoryguj Y
                                                let tx = 0, ty = 0;
                                                const translateMatch = dragStyle.transform.match(/translate(?:3d)?\(\s*([-\d.]+)px,\s*([-\d.]+)px/i);
                                                if (translateMatch) {
                                                    tx = parseFloat(translateMatch[1]);
                                                    ty = parseFloat(translateMatch[2]);
                                                }
                                                // Wyzeruj Y - element będzie śledził kursor
                                                correctedStyle = {
                                                    ...dragStyle,
                                                    transform: `translate(${tx}px, 0px)`
                                                };
                                            }
                                        } else if (!snapshot.isDragging) {
                                            // Wyczyść dane drag po zakończeniu
                                            const element = document.querySelector(`[data-rbd-draggable-id="${draggableId}"]`);
                                            if (element) {
                                                delete element.dataset.dragStartX;
                                                delete element.dataset.dragStartY;
                                                delete element.dataset.dragMouseStartX;
                                                delete element.dataset.dragMouseStartY;
                                            }
                                        }
                                        
                                        return (
                                            <div
                                                ref={provided2.innerRef}
                                                {...provided2.draggableProps}
                                                {...provided2.dragHandleProps}
                                                style={correctedStyle}
                                                className={`staff-item flex justify-between items-center px-2 py-1 mb-1 rounded border
                                                            bg-gray-100 text-gray-900 border-gray-200
                                                            dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700
                                                            ${snapshot.isDragging ? 'is-dragging' : ''}`}
                                            >
                                                <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                                                    <span className="inline-flex items-center justify-center shrink-0">
                                                        <Avatar employee={show} size={18} title={sName} />
                                                    </span>
                                                    <span
                                                        className="staff-item-name min-w-0"
                                                        title={offList ? `${sName} — spoza listy` : `${sName}${tentative ? ' (?)' : ''}`}
                                                    >
                                                        {sName}{tentative ? ' (?)' : ''}
                                                        {offList && <em className="staff-off-list-mark">spoza listy</em>}
                                                    </span>
                                                </div>
                                                {isAdmin && (
                                                    <StaffMenu
                                                        onRemove={() => {
                                                            const updated = [...(evt.staff || [])];
                                                            updated.splice(j, 1);
                                                            const eventIdentifier = evt?.gId ? evt : evt.event;
                                                            updateEventStaff(date, eventIdentifier, updated, evt.calendarId ?? activeCalendarId, true);
                                                        }}
                                                        onToggleTentative={() => {
                                                            const updated = [...(evt.staff || [])];
                                                            const current = updated[j];
                                                            if (typeof current === 'object') updated[j] = { ...current, tentative: !current.tentative };
                                                            else updated[j] = { name: current, tentative: true };
                                                            const eventIdentifier = evt?.gId ? evt : evt.event;
                                                            updateEventStaff(date, eventIdentifier, updated, evt.calendarId ?? activeCalendarId, true);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        );
                                    }}
                                </Draggable>
                            );
                        })}

                        {Array.from({ length: freeSlots }).map((_, i) => (
                            <div
                                key={`free-${i}`}
                                className="staff-item flex items-center px-2 py-1 mb-1 rounded border
                                          text-gray-600 bg-gray-50 border-gray-200
                                          dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700"
                            >
                                <MdPersonAdd className="text-red-500" />
                                <span className="ml-2 text-sm">Wolne miejsce</span>
                            </div>
                        ))}

                        {provided.placeholder}
                    </div>
                )}
            </Droppable>

            {isAdmin && (
                <AddStaffPicker
                    employees={available}
                    assignedNames={assignedNames}
                    getConflictStatus={(name) => getStaffAssignmentConflict(name, assignmentCtx)}
                    onSelect={async (name) => {
                        const nm = staffNameForAssignment(name, empList);
                        if (!nm) return;
                        const already = assignedNames.some(
                            (current) => current.toLowerCase() === nm.toLowerCase()
                        );
                        if (already) return;
                        if (!(await confirmStaffAssignmentIfNeeded(nm, assignmentCtx))) return;
                        const conflict = getStaffAssignmentConflict(nm, assignmentCtx);
                        const newEntry = formatStaffEntryForStore({
                            name: nm,
                            tentative: conflict || false,
                        });
                        const updated = [...(evt.staff || []), newEntry];
                        const normalized = updated.map(s => typeof s === 'string' ? s : s?.name).filter(Boolean);
                        const rawNeeded = Number(evt.needed);
                        const need = Number.isFinite(rawNeeded) && rawNeeded >= 1 ? rawNeeded : Math.max(1, normalized.length);
                        const clampedNeeded = Math.max(need, normalized.length, 1);
                        const eventIdentifier = evt?.gId ? evt : evt.event;
                        const calendarIdToUse = evt.calendarId ?? activeCalendarId;
                        
                        // updateEventStaff automatycznie zarządza needed (zwiększa przy usuwaniu, nie zmienia przy dodawaniu)
                        // Nie wywołujemy updateNeededForEvent tutaj, żeby uniknąć konfliktów
                        updateEventStaff(date, eventIdentifier, updated, calendarIdToUse, true);
                        // Uwaga: updateNeededForEvent nie jest wywoływane tutaj, bo updateEventStaff już zarządza needed
                    }}
                />
            )}

            {openNeededMenu === keyId && (
                <NeededMenu
                    date={date}
                    eventIndex={evtIndex}
                    event={evt}
                    updateNeededForEvent={updateNeededForEvent}
                    anchorEl={actionContainersRef.current[keyId] || null}
                    portalRoot={neededPortalRoot || actionContainersRef.current[keyId] || undefined}
                    onRegisterPanelRef={onRegisterPanelRef}
                    closeMenu={onNeededClose}
                />
            )}

            <EventNoteEditor
                open={openNoteKey === keyId}
                noteText={noteDraft}
                onChange={onNoteChange}
                onSave={onNoteSave}
                onClose={onNoteCancel}
                onClear={onNoteClear}
            />
        </div>
    );
}

